import type { AttentionItem } from '@@/api/attention/attention-items';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { useWebsites } from '@/state/useWebsites';
import { ActionButton, CountBadge, DisclosureButton, EmptyState, Panel, SeverityDot } from '../ConsolePanel';
import { describeAttention, groupBySeverity } from './attention-descriptors';

/**
 * What still needs a person, at the top of the home screen.
 *
 * Rows are current state rather than events, so an item stays until the thing
 * is actually dealt with. Clearing the notification badge does not clear this.
 *
 * One 35px line per item: dot, title, the sentence that explains it, the verb.
 * The severity headings this used to carry are now counts in the panel head —
 * the queue is already sorted urgent-first, so the dot is enough to place a row
 * and three sub-headings only pushed the actual work below the fold.
 */

type AttentionCardProps = {
  items: AttentionItem[];
  isLoading: boolean;
  /**
   * Whether the queue failed to load. Optional because Home owns the fetch and
   * `useAttention` does not surface the flag yet — until it does, a failed request
   * still falls through to the all-clear line below.
   */
  isError?: boolean;
};

/**
 * How many rows show before the list collapses.
 *
 * A queue that fills the screen stops being read, and the sort already puts the
 * urgent groups first, so what is hidden is what matters least.
 */
const VISIBLE_LIMIT = 5;

const keyFor = (item: AttentionItem) => `${item.kind}:${item.experimentId}`;

export const AttentionCard = ({ items, isLoading, isError = false }: AttentionCardProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  // The queue is fetched per website and the query is gated on having one, so with no
  // website picked React Query never runs: isLoading is false and items is empty. Read
  // here rather than taken as a prop because Home passes only the query's own state.
  const { website } = useWebsites();

  /*
    Every one of these keeps the panel rather than returning null. This is the first
    panel on Home, so nothing here let Status, Visitors, the chart and the table render
    at the top of the page and then get pushed down when the queue arrived — usually
    just after the reader had started aiming at one of them.

    Ordered outward from what the panel actually knows, because an empty queue used to
    mean all four states and the one sentence it had was the reassuring one: the landing
    screen reported all-clear with no website picked and on a failed check. "Nothing
    needs you" is only true once a check has run and come back empty.
  */
  if (!website?.id) {
    return (
      <Panel
        title="Needs your attention"
        description="Everything that is waiting on a person."
        tourAnchor="home-attention"
      >
        <EmptyState>Pick a website to see what needs you.</EmptyState>
      </Panel>
    );
  }

  if (isLoading) {
    return (
      <Panel
        title="Needs your attention"
        description="Everything that is waiting on a person."
        tourAnchor="home-attention"
      >
        <EmptyState>Checking what needs you…</EmptyState>
      </Panel>
    );
  }

  if (isError) {
    return (
      <Panel
        title="Needs your attention"
        description="Everything that is waiting on a person."
        tourAnchor="home-attention"
      >
        <EmptyState>Could not check what needs you. Reload the page to try again.</EmptyState>
      </Panel>
    );
  }

  if (items.length === 0) {
    return (
      <Panel
        title="Needs your attention"
        description="Everything that is waiting on a person."
        tourAnchor="home-attention"
      >
        <EmptyState>Nothing needs you right now.</EmptyState>
      </Panel>
    );
  }

  const visible = expanded ? items : items.slice(0, VISIBLE_LIMIT);
  const hidden = items.length - visible.length;
  // Counted over everything, not over what fits: a head that shrinks when the
  // list collapses would report the fold rather than the workload.
  const groups = groupBySeverity(items);

  return (
    <Panel
      title="Needs your attention"
      description="Everything that is waiting on a person."
      tourAnchor="home-attention"
      bodyClassName="p-0"
      actions={groups.map((group) => (
        <CountBadge key={group.severity} severity={group.severity}>
          <span title={group.meta.description}>
            {group.items.length} {group.meta.label}
          </span>
        </CountBadge>
      ))}
    >
      <div className="divide-line divide-y">
        {visible.map((item) => {
          const descriptor = describeAttention(item);
          return (
            /*
              No row-wide `hover:bg-hover` here. The row is not clickable — only the
              ActionButton navigates — and lighting the whole row on hover promised a
              click that did nothing. Health renders the identical row as a real
              button; this one keeps the button at the end, so the hover belongs on
              the button rather than on 100% of the width around it.
            */
            /*
              Three stacked lines below `md`, one row from there.

              At 375px the fixed 35px row gave the detail roughly 65 pixels after the
              title's 45% and the action button, so the sentence explaining why the
              item needs a person was truncated to nothing. Stacking costs height and
              keeps the only text that says what to do.
            */
            <div
              key={keyFor(item)}
              className="flex flex-col gap-1.5 px-3.5 py-2.5 md:h-[35px] md:flex-row md:items-center md:gap-2.5 md:py-0"
            >
              <div className="flex min-w-0 items-center gap-2.5 md:max-w-[45%] md:shrink-0">
                <SeverityDot severity={item.severity} />
                <span className="min-w-0 truncate text-meta" title={descriptor.title(item)}>
                  {descriptor.title(item)}
                </span>
              </div>
              {/* pl-4 lines the detail up under the title past the dot and its gap. */}
              <span className="supporting min-w-0 truncate pl-4 md:flex-1 md:pl-0" title={descriptor.detail(item)}>
                {descriptor.detail(item)}
              </span>
              <ActionButton
                className="ml-4 self-start md:ml-0 md:self-auto"
                onClick={() => navigate(descriptor.href(item))}
                title={`${descriptor.action} — ${descriptor.title(item)}`}
              >
                {descriptor.action}
                <ChevronRight className="size-3" strokeWidth={1.5} />
              </ActionButton>
            </div>
          );
        })}
      </div>

      {(hidden > 0 || expanded) && (
        <div className="border-line flex border-t px-3.5 py-2">
          <DisclosureButton onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? 'Show less' : `Show ${hidden} more`}
          </DisclosureButton>
        </div>
      )}
    </Panel>
  );
};
